'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { ChevronDown, Check } from 'lucide-react';
import { DOCS_SLUG_SET } from '@/lib/developer/docs-content';

const PROD_BASE = 'https://wisprs.co';

const CONFETTI_COLORS = ['#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#F97316'];

function spawnConfetti(origin: HTMLElement) {
  const rect = origin.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  for (let i = 0; i < 22; i++) {
    const el = document.createElement('div');
    const size = 5 + Math.random() * 5;
    const color = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
    const angle = Math.random() * 360;
    const distance = 50 + Math.random() * 70;
    const dx = Math.cos((angle * Math.PI) / 180) * distance;
    const dy = Math.sin((angle * Math.PI) / 180) * distance - 30;
    const rotation = Math.random() * 720 - 360;

    Object.assign(el.style, {
      position: 'fixed',
      left: `${cx - size / 2}px`,
      top: `${cy - size / 2}px`,
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: Math.random() > 0.5 ? '50%' : '2px',
      background: color,
      pointerEvents: 'none',
      zIndex: '9999',
      transition: 'transform 0.6s cubic-bezier(0.25,0.46,0.45,0.94), opacity 0.6s ease',
      opacity: '1',
    });

    document.body.appendChild(el);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.transform = `translate(${dx}px, ${dy}px) rotate(${rotation}deg) scale(0.4)`;
        el.style.opacity = '0';
      });
    });

    setTimeout(() => el.remove(), 700);
  }
}

function getSlugFromPath(pathname: string): string | null {
  if (pathname === '/developer/docs') return 'introduction';
  const match = pathname.match(/^\/developer\/docs\/([^/]+)$/);

  if (!match) return null;
  const slug = match[1];

  return DOCS_SLUG_SET.has(slug) ? slug : null;
}

export function DocsAiToolbar() {
  const pathname = usePathname();
  const slug = getSlugFromPath(pathname);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyBtnRef = useRef<HTMLButtonElement>(null);
  const ref = useRef<HTMLDivElement>(null);

  // SAFETY: The surrounding typed contract establishes the asserted representation.
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCopyPage = useCallback(async () => {
    if (!slug) return;

    try {
      const res = await fetch(`/developer/docs/raw/${slug}`);
      const md = await res.text();
      await navigator.clipboard.writeText(md);
    } catch {
      await navigator.clipboard.writeText(`${PROD_BASE}/developer/docs/raw/${slug}`);
    }

    setCopied(true);

    if (copyBtnRef.current) spawnConfetti(copyBtnRef.current);
    setTimeout(() => setCopied(false), 2500);
  }, [slug]);

  if (!slug) return null;

  const rawRelativeUrl = `/developer/docs/raw/${slug}`;
  const rawProdUrl = `${PROD_BASE}/developer/docs/raw/${slug}`;
  const aiPrompt = encodeURIComponent(`Read from this URL: ${rawProdUrl} and explain it to me.`);
  const claudeUrl = `https://claude.ai/new?q=${aiPrompt}`;
  const chatgptUrl = `https://chatgpt.com/?q=${aiPrompt}`;

  const dropdownItems = [
    {
      label: 'View as Markdown',
      description: 'Open raw markdown in a new tab',
      href: rawRelativeUrl,
      icon: (
        <span className="flex h-5 w-5 items-center justify-center text-[9px] font-bold text-[rgb(var(--foreground-secondary))]">
          MD
        </span>
      ),
    },
    {
      label: 'Open in Claude',
      description: 'Ask questions about this page',
      href: claudeUrl,
      icon: (
        <Image
          src="/logos/claude-logo.png"
          alt="Claude"
          width={20}
          height={20}
          className="rounded"
        />
      ),
    },
    {
      label: 'Open in ChatGPT',
      description: 'Ask questions about this page',
      href: chatgptUrl,
      icon: (
        <Image
          src="/logos/chatgpt-logo.webp"
          alt="ChatGPT"
          width={20}
          height={20}
          className="rounded"
        />
      ),
    },
  ];

  return (
    <div ref={ref} className="relative mb-6 flex justify-end">
      <div className="flex items-stretch overflow-hidden rounded-lg border border-[rgb(var(--surface-border))]">
        <button
          ref={copyBtnRef}
          onClick={handleCopyPage}
          disabled={copied}
          className="flex items-center gap-2 bg-[rgb(var(--surface))] px-4 py-1.5 text-sm font-medium text-[rgb(var(--foreground-secondary))] transition-colors hover:text-[rgb(var(--foreground))] disabled:cursor-default"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-green-400" />
              <span className="text-green-400">Copied!</span>
            </>
          ) : (
            'Copy page'
          )}
        </button>
        <div className="w-px bg-[rgb(var(--surface-border))]" />
        <button
          onClick={() => setOpen((v) => !v)}
          aria-label="More options"
          className="flex items-center bg-[rgb(var(--surface))] px-2 py-1.5 text-[rgb(var(--foreground-secondary))] transition-colors hover:text-[rgb(var(--foreground))]"
        >
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1.5 w-64 overflow-hidden rounded-xl border border-[rgb(var(--surface-border))] bg-[rgb(var(--background))] shadow-xl">
          {dropdownItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-[rgb(var(--surface))]"
            >
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))]">
                {item.icon}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-[rgb(var(--foreground))]">{item.label}</p>
                <p className="text-xs text-[rgb(var(--foreground-secondary))]">
                  {item.description}
                </p>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
